/**
 * Lost Review Pool Component
 * ==========================
 * Shows customers pending Lost review for City Heads to action.
 * 
 * Features:
 * - Aging metrics (0-3 days, 3-7 days, 7+ days)
 * - Actions: Confirm Lost, Reassign
 * - Recovery rate tracking
 * 
 * Workflow:
 * - Rep marks lead as Lost → Status becomes PENDING_LOST_REVIEW
 * - City Head reviews and either:
 *   - Confirm Lost → Status becomes Lost
 *   - Reassign → Lead becomes Active (Warm), source=Retargeting
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { 
  Clock, UserX, RefreshCw, UserPlus, Check, X, 
  AlertTriangle, TrendingUp, ChevronRight, Phone,
  Calendar, User
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const LostReviewPool = ({ city, userCity, onRefresh, onActionComplete }) => {
  // Use city or userCity prop (for backward compatibility)
  const effectiveCity = city || userCity;
  
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [agingMetrics, setAgingMetrics] = useState({ '0_3_days': 0, '3_7_days': 0, '7_plus_days': 0, total: 0 });
  const [agingFilter, setAgingFilter] = useState('all');
  const [sortBy, setSortBy] = useState('oldest');
  const [total, setTotal] = useState(0);
  
  // Action dialog state
  const [actionDialog, setActionDialog] = useState({ open: false, customer: null, action: null });
  const [actionRemark, setActionRemark] = useState('');
  const [newRepId, setNewRepId] = useState('');
  const [newStage, setNewStage] = useState('Warm');
  const [processing, setProcessing] = useState(false);
  
  // Reps list for reassignment
  const [reps, setReps] = useState([]);
  
  // Recovery metrics
  const [recoveryMetrics, setRecoveryMetrics] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort_by: sortBy, limit: 50 };
      if (effectiveCity) params.city = effectiveCity;
      if (agingFilter !== 'all') params.aging_filter = agingFilter;
      
      const [poolRes, metricsRes] = await Promise.all([
        api.getLostReviewPool(params),
        api.getLostRecoveryMetrics({ period: 'mtd', city: effectiveCity })
      ]);
      
      if (poolRes.data) {
        setCustomers(poolRes.data.customers || []);
        setAgingMetrics(poolRes.data.aging_metrics || {});
        setTotal(poolRes.data.total || 0);
      }
      
      if (metricsRes.data) {
        setRecoveryMetrics(metricsRes.data.metrics);
      }
    } catch (error) {
      console.error('Failed to fetch lost review pool:', error);
      toast.error('Failed to load lost review pool');
    } finally {
      setLoading(false);
    }
  }, [effectiveCity, agingFilter, sortBy]);

  const fetchReps = useCallback(async () => {
    try {
      // Pass city filter to get reps from the same city
      const params = {};
      if (effectiveCity) params.city = effectiveCity;
      const res = await api.getTeamMembers(params);
      if (res.data) {
        // Handle both array response and {users: []} response
        const users = Array.isArray(res.data) ? res.data : (res.data.users || []);
        const repList = users.filter(u => ['Sales', 'Rep', 'sales_rep'].includes(u.role) && u.is_active !== false);
        setReps(repList);
      }
    } catch (error) {
      console.error('Failed to fetch reps:', error);
    }
  }, [effectiveCity]);

  useEffect(() => {
    fetchData();
    fetchReps();
  }, [fetchData, fetchReps]);

  const handleAction = async () => {
    if (!actionDialog.customer || !actionDialog.action) return;
    
    if (actionDialog.action === 'REASSIGN' && !newRepId) {
      toast.error('Please select a rep to reassign to');
      return;
    }
    
    setProcessing(true);
    try {
      const res = await api.processLostReviewAction(
        actionDialog.customer.customer_id,
        actionDialog.action,
        actionRemark,
        actionDialog.action === 'REASSIGN' ? newRepId : null,
        actionDialog.action !== 'CONFIRM_LOST' ? newStage : null
      );
      
      if (res.data) {
        toast.success(res.data.message);
        setActionDialog({ open: false, customer: null, action: null });
        setActionRemark('');
        setNewRepId('');
        setNewStage('Warm');
        fetchData();
        // Call the appropriate callback
        if (onActionComplete) onActionComplete();
        else if (onRefresh) onRefresh();
      }
    } catch (error) {
      console.error('Action failed:', error);
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setProcessing(false);
    }
  };

  const openActionDialog = (customer, action) => {
    setActionDialog({ open: true, customer, action });
    setActionRemark('');
    setNewRepId('');
    setNewStage(customer.stage_before_lost || 'Warm');
  };

  const getAgingBadge = (days) => {
    if (days <= 3) return <Badge className="bg-green-100 text-green-700">{days}d</Badge>;
    if (days <= 7) return <Badge className="bg-yellow-100 text-yellow-700">{days}d</Badge>;
    return <Badge className="bg-red-100 text-red-700">{days}d</Badge>;
  };

  const getActionTitle = (action) => {
    switch (action) {
      case 'CONFIRM_LOST': return 'Confirm as Lost';
      case 'REASSIGN': return 'Reassign Lead';
      default: return 'Action';
    }
  };

  return (
    <div className="space-y-4" data-testid="lost-review-pool">
      {/* Header with Recovery Metrics */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Lost Review Pool</h2>
          <p className="text-sm text-slate-500">Review and action leads marked as Lost by reps</p>
        </div>
        
        {recoveryMetrics && (
          <Card className="p-3 bg-emerald-50 border-emerald-200">
            <div className="flex items-center gap-3">
              <TrendingUp className="text-emerald-600" size={20} />
              <div>
                <p className="text-xs text-emerald-600">MTD Recovery Rate</p>
                <p className="text-lg font-bold text-emerald-700">{recoveryMetrics.recovery_rate_percent}%</p>
              </div>
              <div className="text-xs text-emerald-600 border-l border-emerald-300 pl-3">
                <p>Recovered: {recoveryMetrics.total_recovered}</p>
                <p>Total: {recoveryMetrics.total_submitted - recoveryMetrics.pending_review}</p>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Aging Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card 
          className={`p-3 cursor-pointer transition-all ${agingFilter === 'all' ? 'ring-2 ring-purple-500' : 'hover:shadow-md'}`}
          onClick={() => setAgingFilter('all')}
          data-testid="aging-filter-all"
        >
          <p className="text-xs text-slate-500">Total Pending</p>
          <p className="text-2xl font-bold text-purple-600">{agingMetrics.total || 0}</p>
        </Card>
        <Card 
          className={`p-3 cursor-pointer transition-all ${agingFilter === '0-3' ? 'ring-2 ring-green-500' : 'hover:shadow-md'}`}
          onClick={() => setAgingFilter('0-3')}
          data-testid="aging-filter-0-3"
        >
          <p className="text-xs text-slate-500">0-3 Days</p>
          <p className="text-2xl font-bold text-green-600">{agingMetrics['0_3_days'] || 0}</p>
        </Card>
        <Card 
          className={`p-3 cursor-pointer transition-all ${agingFilter === '3-7' ? 'ring-2 ring-yellow-500' : 'hover:shadow-md'}`}
          onClick={() => setAgingFilter('3-7')}
          data-testid="aging-filter-3-7"
        >
          <p className="text-xs text-slate-500">3-7 Days</p>
          <p className="text-2xl font-bold text-yellow-600">{agingMetrics['3_7_days'] || 0}</p>
        </Card>
        <Card 
          className={`p-3 cursor-pointer transition-all ${agingFilter === '7+' ? 'ring-2 ring-red-500' : 'hover:shadow-md'}`}
          onClick={() => setAgingFilter('7+')}
          data-testid="aging-filter-7plus"
        >
          <p className="text-xs text-slate-500">7+ Days</p>
          <p className="text-2xl font-bold text-red-600">{agingMetrics['7_plus_days'] || 0}</p>
        </Card>
      </div>

      {/* Sort Control */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-500">Sort by:</span>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-32 h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="oldest">Oldest First</SelectItem>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="rep_name">Rep Name</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" onClick={fetchData} disabled={loading}>
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Customer List */}
      {loading ? (
        <div className="text-center py-8 text-slate-500">Loading...</div>
      ) : customers.length === 0 ? (
        <Card className="p-8 text-center">
          <Check className="mx-auto mb-3 text-green-500" size={40} />
          <p className="text-slate-600">No leads pending review!</p>
          <p className="text-sm text-slate-400">All lost leads have been processed.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {customers.map((customer) => (
            <Card key={customer.customer_id} className="p-4" data-testid={`lost-review-item-${customer.customer_id}`}>
              <div className="flex flex-col sm:flex-row justify-between gap-3">
                {/* Customer Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-slate-800">{customer.customer_name || 'Unknown'}</span>
                    {getAgingBadge(customer.days_pending || 0)}
                    <Badge variant="outline" className="text-xs">
                      Was: {customer.stage_before_lost || 'Warm'}
                    </Badge>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <User size={12} />
                      {customer.rep_name || 'Unknown Rep'}
                    </span>
                    {customer.customer_phone && (
                      <span className="flex items-center gap-1">
                        <Phone size={12} />
                        {customer.customer_phone}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {customer.lost_submitted_at?.slice(0, 10)}
                    </span>
                  </div>
                  
                  <div className="mt-2 p-2 bg-red-50 rounded text-sm">
                    <span className="text-red-700 font-medium">Lost Reason:</span>{' '}
                    <span className="text-red-600">{customer.lost_reason}</span>
                    {customer.lost_remark && (
                      <p className="text-red-500 text-xs mt-1 italic">"{customer.lost_remark}"</p>
                    )}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex sm:flex-col gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-blue-300 text-blue-600 hover:bg-blue-50"
                    onClick={() => openActionDialog(customer, 'REASSIGN')}
                    data-testid={`action-reassign-${customer.customer_id}`}
                  >
                    <UserPlus size={14} className="mr-1" />
                    Reassign
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    onClick={() => openActionDialog(customer, 'CONFIRM_LOST')}
                    data-testid={`action-confirm-lost-${customer.customer_id}`}
                  >
                    <X size={14} className="mr-1" />
                    Confirm Lost
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, customer: null, action: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{getActionTitle(actionDialog.action)}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {actionDialog.customer && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{actionDialog.customer.customer_name}</p>
                <p className="text-sm text-slate-500">Rep: {actionDialog.customer.rep_name}</p>
                <p className="text-sm text-red-500">Reason: {actionDialog.customer.lost_reason}</p>
              </div>
            )}
            
            {actionDialog.action === 'REASSIGN' && (
              <>
                <div>
                  <label className="text-sm font-medium text-slate-700">Assign to Rep *</label>
                  <Select value={newRepId} onValueChange={setNewRepId}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select a rep..." />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Show current rep first with indicator */}
                      {reps.map(rep => (
                        <SelectItem key={rep.user_id} value={rep.user_id}>
                          {rep.rep_name || rep.full_name || rep.email}
                          {rep.user_id === actionDialog.customer?.rep_user_id && ' (Current)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Info about Retargeting */}
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700 font-medium">📌 Retargeting Lead</p>
                  <p className="text-xs text-blue-600 mt-1">
                    After reassignment, lead will be marked as:
                  </p>
                  <ul className="text-xs text-blue-600 mt-1 list-disc list-inside">
                    <li>Source: <strong>Retargeting</strong></li>
                    <li>Sub-Source: <strong>Lost Lead Recovery</strong></li>
                    <li>Status: <strong>Active (Warm)</strong></li>
                  </ul>
                </div>
              </>
            )}
            
            <div>
              <label className="text-sm font-medium text-slate-700">Remark (Optional)</label>
              <Textarea
                value={actionRemark}
                onChange={(e) => setActionRemark(e.target.value)}
                placeholder="Add a note about this decision..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, customer: null, action: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleAction} 
              disabled={processing || (actionDialog.action === 'REASSIGN' && !newRepId)}
              className={actionDialog.action === 'CONFIRM_LOST' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}
            >
              {processing ? 'Processing...' : getActionTitle(actionDialog.action)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LostReviewPool;
